import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type {
  ApprovalRequest,
  ChatAttachment,
  ChatError,
  DownloadLink,
  Message,
  StreamingStatus,
} from '../models';

export type { ChatError, Message, StreamingStatus } from '../models';

type SentMessagePayload = {
  content: string;
  companyCode: string;
  attachments?: ChatAttachment[];
};

type FinalResponsePayload = {
  content: string;
  downloadLinks: DownloadLink[];
};

type TypingCompletedPayload = {
  messageId: string;
};

/** The latest assistant message only when it is still typing (not yet completed). */
function findLastIncompleteAssistantMessage(messages: Message[]): Message | undefined {
  const latestAssistantMessage = messages.findLast((message) => message.role === 'assistant');
  return latestAssistantMessage?.isTypingComplete ? undefined : latestAssistantMessage;
}

/** Stash the final content/links on a still-typing message for the typewriter to apply. */
function queueFinalResponseOnTypingMessage(message: Message, payload: FinalResponsePayload): void {
  message.pendingFinalContent = payload.content || undefined;
  message.pendingDownloadLinks = payload.downloadLinks;
}

function createCompletedAssistantMessage(payload: FinalResponsePayload): Message {
  return {
    id: crypto.randomUUID(),
    role: 'assistant',
    content: payload.content,
    timestamp: new Date(),
    downloadLinks: payload.downloadLinks,
    isTypingComplete: true,
  };
}

/** Move any queued final content/links onto the message and mark it complete. */
function applyPendingFinalResponse(message: Message): void {
  if (message.pendingFinalContent) {
    message.content = message.pendingFinalContent;
    message.pendingFinalContent = undefined;
  }
  message.downloadLinks = message.pendingDownloadLinks ?? [];
  message.pendingDownloadLinks = undefined;
  message.isTypingComplete = true;
}

export interface AgentChatState {
  messages: Message[];
  isLoading: boolean;
  streamingStatus: StreamingStatus;
  currentToolName: string | null;
  progressMessage: string | null;
  error: ChatError | null;
  approvalRequest: ApprovalRequest | null;
  skipTypewriter: boolean;
  threadId: string;
  /** 再試行はこの参照をそのまま送る（再アップロードしない） */
  lastSent: SentMessagePayload | null;
}

const initialState: AgentChatState = {
  messages: [],
  isLoading: false,
  streamingStatus: 'idle',
  currentToolName: null,
  progressMessage: null,
  error: null,
  approvalRequest: null,
  skipTypewriter: false,
  threadId: crypto.randomUUID(),
  lastSent: null,
};

export const agentChatSlice = createSlice({
  name: 'agentChat',
  initialState,
  reducers: {
    messageSent(state, action: PayloadAction<SentMessagePayload>) {
      state.messages.push({
        id: crypto.randomUUID(),
        role: 'user',
        content: action.payload.content,
        timestamp: new Date(),
        attachments: action.payload.attachments,
      });
      state.isLoading = true;
      state.streamingStatus = 'thinking';
      state.error = null;
      state.approvalRequest = null;
      state.skipTypewriter = false;
      state.lastSent = action.payload;
    },

    tokenReceived(state, action: PayloadAction<string>) {
      const last = state.messages[state.messages.length - 1];
      if (last?.role === 'assistant') {
        last.content = action.payload;
      } else {
        state.messages.push({
          id: crypto.randomUUID(),
          role: 'assistant',
          content: action.payload,
          timestamp: new Date(),
        });
      }
      state.streamingStatus = 'streaming';
    },

    progressUpdated(state, action: PayloadAction<string>) {
      state.progressMessage = action.payload;
    },

    toolStarted(state, action: PayloadAction<{ toolName: string; displayName?: string }>) {
      state.streamingStatus = 'tool_calling';
      state.currentToolName = action.payload.displayName || action.payload.toolName;
      state.progressMessage = action.payload.displayName || action.payload.toolName;
    },

    finalResponseQueued(state, action: PayloadAction<FinalResponsePayload>) {
      const typingMessage = findLastIncompleteAssistantMessage(state.messages);
      if (typingMessage) {
        queueFinalResponseOnTypingMessage(typingMessage, action.payload);
      } else if (action.payload.content) {
        state.messages.push(createCompletedAssistantMessage(action.payload));
        state.skipTypewriter = true;
      }
    },

    typingCompleted(state, action: PayloadAction<TypingCompletedPayload>) {
      const msg = state.messages.find((m) => m.id === action.payload.messageId);
      if (msg?.role !== 'assistant') {
        return;
      }
      applyPendingFinalResponse(msg);
    },

    streamEnded(
      state,
      action: PayloadAction<{
        approvalRequest: ApprovalRequest | null;
      }>,
    ) {
      state.isLoading = false;
      state.streamingStatus = 'idle';
      state.currentToolName = null;
      state.approvalRequest = action.payload.approvalRequest;
    },

    threadIdUpdated(state, action: PayloadAction<string>) {
      if (action.payload) {
        state.threadId = action.payload;
      }
    },

    approvalSent(state, action: PayloadAction<string>) {
      state.messages.push({
        id: crypto.randomUUID(),
        role: 'user',
        content: action.payload,
        timestamp: new Date(),
      });
      state.isLoading = true;
      state.streamingStatus = 'thinking';
      state.error = null;
      state.approvalRequest = null;
    },

    errorOccurred(state, action: PayloadAction<ChatError>) {
      state.isLoading = false;
      state.streamingStatus = 'idle';
      state.currentToolName = null;
      state.progressMessage = null;
      state.error = action.payload;
    },

    errorCleared(state) {
      state.error = null;
    },

    chatCleared(state) {
      Object.assign(state, { ...initialState, threadId: crypto.randomUUID() });
    },

    messagesRestored(state, action: PayloadAction<{ threadId: string; messages: Message[] }>) {
      Object.assign(state, {
        ...initialState,
        threadId: action.payload.threadId,
        messages: action.payload.messages,
        skipTypewriter: true,
      });
    },
  },
});

export const {
  messageSent,
  tokenReceived,
  finalResponseQueued,
  typingCompleted,
  progressUpdated,
  toolStarted,
  streamEnded,
  threadIdUpdated,
  approvalSent,
  errorOccurred,
  errorCleared,
  chatCleared,
  messagesRestored,
} = agentChatSlice.actions;
